// The notes on one deal, with a search that only asks about this deal.
//
// The scoped search is the panel's reason to exist beyond a list: a deal that
// has been running a year has more notes than anybody scrolls, and "what did we
// say about the go-live date" is the question people actually arrive with.

import { useState, type ReactNode } from "react";
import styles from "./DealNotesPanel.module.css";
import { Card, CardHeader } from "../../design/Card.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { SearchInput } from "../../design/SearchInput.js";
import { NoteList } from "./NoteList.js";
import { useDealNotes, useNoteSearch } from "../../api/queries.js";

export interface DealNotesPanelProps {
  dealId: string;
  /** Opened on mount, so a search result can link straight to the note it found. */
  openNoteId?: string | null;
}

export function DealNotesPanel({ dealId, openNoteId }: DealNotesPanelProps): ReactNode {
  const [query, setQuery] = useState("");
  const notes = useDealNotes(dealId);
  const search = useNoteSearch(query, dealId);
  const searching = query.trim() !== "";

  return (
    <Card>
      <CardHeader
        title="Notes"
        description="What was said about this deal — meetings the notetaker wrote up, and anything typed by hand."
        actions={
          <SearchInput
            value={query}
            onCommit={setQuery}
            placeholder="Search these notes…"
            ariaLabel="Search this deal's notes"
            className={styles.search}
          />
        }
      />

      {searching ? (
        <QueryBoundary query={search} label="the note search">
          {(data) => (
            <div className={styles.results}>
              {data.items.length === 0 ? (
                <p className={styles.empty}>
                  Nothing in this deal's notes matched “{query.trim()}”.
                  {data.pending_notes > 0 && (
                    <>
                      {" "}
                      {data.pending_notes} note{data.pending_notes === 1 ? " is" : "s are"} still
                      being indexed.
                    </>
                  )}
                </p>
              ) : (
                data.items.map((hit) => (
                  <div key={`${hit.note_id}:${hit.chunk_ordinal}`} className={styles.hit}>
                    <div className={styles.hitTitle}>
                      {hit.title ?? "Untitled note"}
                      {hit.occurred_at !== null && (
                        <span className={styles.hitDate}>
                          {new Date(hit.occurred_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    {/* The passage that matched, verbatim — never a summary, so
                        the reader judges the match rather than trusting it. */}
                    <p className={styles.hitSnippet}>{hit.snippet}</p>
                  </div>
                ))
              )}
            </div>
          )}
        </QueryBoundary>
      ) : (
        <QueryBoundary query={notes} label="this deal's notes">
          {(data) => (
            <NoteList
              notes={data.items}
              initiallyOpen={openNoteId ?? null}
              empty="No notes on this deal yet."
            />
          )}
        </QueryBoundary>
      )}
    </Card>
  );
}
